import { createContext, useState, useEffect, useContext } from "react";

import dayjs from "dayjs";

import FormulationPickerContext from "./FormulationPickerContext";
import NotificationContext from "../NotificationContext";
import useAxios from "../../utils/useAxios";
import { resObj } from "../../utils/constants";

const EncapsulantPhaseContext = createContext();

export default EncapsulantPhaseContext;

export const EncapsulantPhaseProvider = ({ children }) => {
  const api = useAxios();
  const { 
    formulationsObj, setFormulationsObj, formulationsArray, 
    setFormulationsArray, setFormulationFormErrors 
  } = useContext(FormulationPickerContext);
  const { getNotificationCount } = useContext(NotificationContext);

  const initValues = {
    today: dayjs(Date.now()),
    operatorArray: [],
    solventArray: [],
    lipidStocksArray: [],
  };
  const [initialValues, setInitialValues] = useState(initValues);
  const [uploadInitValues, setUploadInitValues] = useState({file: null});

  const encapsulantPhaseObj = {
    operatorArray: [],
    solvent: null,
    datePrepared: null,
    lipidArray: [],
  };
  const tabArray = ['Manual', 'File Upload'];
  const [encapsulantPhaseData, setEncapsulantPhaseData] = useState(encapsulantPhaseObj);
  const [apiRes, setApiRes] = useState(resObj);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [tabValue, setTabValue] = useState('Manual');

  const handleTabChange = (e) => {
    setTabValue(e.target.textContent);
  };

  useEffect(() => {
    getInitialData();
    getOperators();
  }, []);

  const getInitialData = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get(
        '/api/materials-capture/encapsulant-phase/initial/data/get'
      );
      
      if (response.status === 200) {
        setInitialValues(prev => ({
          ...prev,
          solventArray: response.data.solvent_list,
          lipidStocksArray: response.data.lipid_stocks_list,
        }));
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Encapsulant Phase Init Data Get',
        loading: false,
      });
    }
  };

  const getOperators = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get('/api/materials-capture/operators/get');
      
      if (response.status === 200) {        
        setInitialValues(prev => ({
          ...prev,
          operatorArray: response.data,
        }))
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Operators Get',
        loading: false,
      });
    }
  };

  const getLipids = async () => {    
    try {      
      setApiRes({...apiRes, loading: true});
      const response = await api.get(
        '/api/materials-capture/encapsulant-phase/lipids/get', {
          params: {
            formulation_type: formulationsObj?.type,
            id_list: formulationsObj?.ids.map(item => +item.id)
          }
        });
      
      if (response.status === 200) {
        const dataArray = response.data;        
        dataArray.forEach(obj => {
          obj['unique_lipid_stock_id'] = null;
        });
        setEncapsulantPhaseData(prev => ({
          ...prev,
          lipidArray: dataArray,
        }));
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Lipids Get',
        loading: false,
      });
    }
  };

  const validate = () => {
    const errors = {};

    if (tabValue === 'Manual') {
      if (formulationsObj?.ids.length === 0) {
        errors['formulation'] = `${formulationsObj?.type} is required!`;
        setFormulationFormErrors(errors);
      }
      if (encapsulantPhaseData?.operatorArray.length === 0) {
        errors['operator'] = 'Operator is required!';
      }
      if (!encapsulantPhaseData?.solvent) {
        errors['solvent'] = 'Solvent is required!';
      }
      if (!encapsulantPhaseData?.datePrepared) {
        errors['datePrepared'] = 'Date prepared is required!';
      }
      if (encapsulantPhaseData?.lipidArray.length === 0) {
        errors['formulation'] = `${formulationsObj?.type} is required!`;
      }    

      encapsulantPhaseData?.lipidArray.forEach((item, index) => {
        if (!item.lipid_name) {
          errors[`lipid_name-${index}`] = 'Lipid name is required!';
        }
        if (!item.unique_lipid_stock_id) {
          errors[`unique_lipid_stock_id-${index}`] = 'Lipid Stock ID is required!';
        }
      });
    } else {
      if (uploadInitValues?.file === null) {
        errors['file'] = 'File is required!';
      }
    }

    return errors;
  };

  const handleLipidStockIdChange = (index, value) => {    
    setEncapsulantPhaseData((prev) => {
      const lipidArray = [...(prev?.lipidArray || [])];

      if (index !== -1) {
        lipidArray[index] = {
          ...lipidArray[index],
          unique_lipid_stock_id: value,
        };
      }

      return {
        ...prev,
        lipidArray,
      };
    });
  };

  const handleSubmit = () => {
    setFormErrors(validate());
    setIsSubmit(true);
  };

  useEffect(() => {    
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      if (tabValue === 'Manual') {
        createEncapsulantPhase();
      } else {
        uploadEncapsulationFile();
      }
    }
  }, [formErrors]);

  const createEncapsulantPhase = async () => {
    try {
      setApiRes({...apiRes, loading: true});

      const response = await api.post(
        '/api/materials-capture/encapsulant-phase/create', {
        formulation_type: formulationsObj?.type,
        formulation_id_list: formulationsObj?.ids.map(item => +item.id),
        operator_list: encapsulantPhaseData?.operatorArray,
        solvent: encapsulantPhaseData?.solvent,
        date_prepared: encapsulantPhaseData?.datePrepared,
        lipid_list: encapsulantPhaseData?.lipidArray,
      });

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Encapsulant Phase create',
        loading: false,
      });
    }
  };

  const uploadEncapsulationFile = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const formData = new FormData();
      formData.append("file", uploadInitValues?.file);

      const response = await api.post(
        '/api/materials-capture/encapsulant-phase/file/upload', formData
      );

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
        setUploadInitValues(initialValues);
        getNotificationCount();
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Encapsulation Details File Upload',
        loading: false,
      });
    }
  };

  const contextData = {
    initialValues: initialValues,
    encapsulantPhaseData: encapsulantPhaseData,
    setEncapsulantPhaseData: setEncapsulantPhaseData,
    formErrors: formErrors,
    handleSubmit: handleSubmit,
    apiRes: apiRes,
    setApiRes: setApiRes,
    formulationsObj: formulationsObj,
    setFormulationsObj: setFormulationsObj,
    formulationsArray: formulationsArray,
    setFormulationsArray: setFormulationsArray,
    getLipids: getLipids,
    handleLipidStockIdChange: handleLipidStockIdChange,
    tabArray: tabArray,
    handleTabChange: handleTabChange,
    tabValue: tabValue,
    uploadInitValues: uploadInitValues,
    setUploadInitValues: setUploadInitValues,
  };

  return (
    <EncapsulantPhaseContext.Provider value={contextData}>
      {children}
    </EncapsulantPhaseContext.Provider>
  )
};